import { deleteApi, getApi, postApi, putApi } from "../helpers/auth_helpers";

const base = process.env.REACT_APP_SERVERURL || "";

export const getKarkhanaStatus = () => getApi(`${base}/karkhana/status`);

export const listKarkhanaWorkTypes = (offset = 0, limit = 50) =>
  getApi(`${base}/karkhana/work-types?offset=${offset}&limit=${limit}`);

export const createKarkhanaWorkType = (body: Record<string, unknown>) => postApi(`${base}/karkhana/work-types`, body);

export const updateKarkhanaWorkType = (id: string, body: Record<string, unknown>) =>
  putApi(`${base}/karkhana/work-types/${id}`, body);

export const listKarkhanaDesigns = (params: { offset?: number; limit?: number; q?: string } = {}) => {
  const q = new URLSearchParams();
  q.set("offset", String(params.offset ?? 0));
  q.set("limit", String(params.limit ?? 50));
  if (params.q) {
    q.set("q", params.q);
  }
  return getApi(`${base}/karkhana/designs?${q.toString()}`);
};

export const createKarkhanaDesign = (body: Record<string, unknown>) =>
  postApi(`${base}/karkhana/designs`, body);

export const updateKarkhanaDesign = (id: string, body: Record<string, unknown>) =>
  putApi(`${base}/karkhana/designs/${id}`, body);

export const deleteKarkhanaDesign = (id: string) =>
  deleteApi(`${base}/karkhana/designs/${id}`, {});

export const listKarkhanaWorkerProfiles = (offset = 0, limit = 50) =>
  getApi(`${base}/karkhana/worker-profiles?offset=${offset}&limit=${limit}`);

export const listKarkhanaWorkerCandidates = () =>
  getApi(`${base}/karkhana/worker-candidates`);

export const createKarkhanaWorkerProfile = (body: Record<string, unknown>) =>
  postApi(`${base}/karkhana/worker-profiles`, body);

export const updateKarkhanaWorkerProfile = (id: string, body: Record<string, unknown>) =>
  putApi(`${base}/karkhana/worker-profiles/${id}`, body);

/** Studio admin confirms a worker a supervisor added, so their pay can be entered. */
export const confirmKarkhanaWorkerProfile = (id: string) =>
  postApi(`${base}/karkhana/worker-profiles/${id}/confirm`, {});

export const searchKarkhanaWorkers = (params: { offset?: number; limit?: number; q?: string; waiting?: boolean } = {}) => {
  const q = new URLSearchParams();
  q.set("offset", String(params.offset ?? 0));
  q.set("limit", String(params.limit ?? 50));
  if (params.q) {
    q.set("q", params.q);
  }
  if (params.waiting) {
    q.set("waiting", "true");
  }
  return getApi(`${base}/karkhana/worker-profiles?${q.toString()}`);
};

/** How many workers a supervisor added still wait for the owner's confirmation (every page, this studio). */
export const getPendingWorkerCount = () => getApi(`${base}/karkhana/worker-profiles/pending-count`);

/** The code a new person of this work would get, without taking it. */
export const getWorkerCodePreview = (prefix: string) =>
  getApi(`${base}/karkhana/worker-code-preview?prefix=${encodeURIComponent(prefix)}`);

export const listWorkerCategories = () => getApi(`${base}/karkhana/worker-categories`);

export const seedWorkerCategories = (template: "BANARASI" | "GENERIC") =>
  postApi(`${base}/karkhana/worker-categories/seed?template=${template}`, {});

export const createWorkerCategory = (body: Record<string, unknown>) =>
  postApi(`${base}/karkhana/worker-categories`, body);

export const updateWorkerCategory = (id: string, body: Record<string, unknown>) =>
  putApi(`${base}/karkhana/worker-categories/${id}`, body);

/** HKP posts a category can give its workers (no admin posts). */
export const listWorkerCategoryPosts = () => getApi(`${base}/karkhana/worker-categories/posts`);

/** Org admin: save every worker without a user as an HKP user. Returns {created, linked, skipped}. */
export const syncKarkhanaWorkerUsers = () => postApi(`${base}/karkhana/worker-profiles/sync-users`, {});

/** Studio admin only: a worker's bank details, Aadhaar (masked) and photo links. */
export const getWorkerKyc = (workerId: string) => getApi(`${base}/karkhana/worker-profiles/${workerId}/kyc`);

/** Null leaves a field as it is, "" clears it. Aadhaar is sent masked ("XXXX XXXX 1234"). */
export const saveWorkerKyc = (workerId: string, body: Record<string, string | null | undefined>) =>
  putApi(`${base}/karkhana/worker-profiles/${workerId}/kyc`, body);

/** Private upload address for a photo: {uploadUrl, key}. */
export const workerKycPhotoUploadUrl = (workerId: string, kind: string, fileType: string) =>
  postApi(`${base}/karkhana/worker-profiles/${workerId}/kyc/photos/${kind}/upload-url`, { fileType });

export const attachWorkerKycPhoto = (workerId: string, kind: string, key: string) =>
  putApi(`${base}/karkhana/worker-profiles/${workerId}/kyc/photos/${kind}`, { key });

export const removeWorkerKycPhoto = (workerId: string, kind: string) =>
  deleteApi(`${base}/karkhana/worker-profiles/${workerId}/kyc/photos/${kind}`);

export const getWorkerCodePolicy = () => getApi(`${base}/karkhana/worker-code-policy`);

export const updateWorkerCodePolicy = (body: Record<string, unknown>) =>
  putApi(`${base}/karkhana/worker-code-policy`, body);

/**
 * Multipart upload; the JSON helpers can't send files. {@code mapping} re-reads the same file with
 * the columns picked on the preview screen, e.g. { name: 0, phone: 2, work: -1 }.
 */
export const uploadWorkerImportFile = async (file: File, mapping?: Record<string, number>) => {
  const form = new FormData();
  form.append("file", file);
  const token = localStorage.getItem("token");
  const m = mapping ? Object.entries(mapping).map(([k, v]) => `${k}:${v}`).join(",") : "";
  const res = await fetch(`${base}/karkhana/worker-imports/file${m ? `?mapping=${encodeURIComponent(m)}` : ""}`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  return res.json();
};

/** Designs and rates file: check (nothing saved) or import as drafts. */
export const uploadRateImport = async (file: File, commit: boolean) => {
  const form = new FormData();
  form.append("file", file);
  const token = localStorage.getItem("token");
  const res = await fetch(`${base}/karkhana/rate-imports/${commit ? "commit" : "preview"}`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  return res.json();
};

/** Set up from whatever file the studio has: check (nothing saved), then apply the same file. */
export const uploadSetupImport = async (file: File, step: "check" | "apply", approveRates = true) => {
  const form = new FormData();
  form.append("file", file);
  const token = localStorage.getItem("token");
  const q = step === "apply" ? `?approveRates=${approveRates}` : "";
  const res = await fetch(`${base}/karkhana/setup-imports/${step}${q}`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  return res.json();
};

export const getSetupTemplate = () => getApi(`${base}/karkhana/setup-imports/template`);

export const getSetupPrompt = () => getApi(`${base}/karkhana/setup-imports/prompt`);

export const listWorkerImports = () => getApi(`${base}/karkhana/worker-imports?offset=0&limit=20`);

export const getWorkerImport = (id: string) => getApi(`${base}/karkhana/worker-imports/${id}`);

export const commitWorkerImport = (id: string) =>
  postApi(`${base}/karkhana/worker-imports/${id}/commit`, {});

export const listWorkerImportRows = (
  id: string,
  params: { status?: string; issue?: string; offset?: number; limit?: number } = {}
) => {
  const q = new URLSearchParams();
  q.set("offset", String(params.offset ?? 0));
  q.set("limit", String(params.limit ?? 200));
  if (params.status) q.set("status", params.status);
  if (params.issue) q.set("issue", params.issue);
  return getApi(`${base}/karkhana/worker-imports/${id}/rows?${q.toString()}`);
};

export const listWorkerReview = (params: { issue?: string; offset?: number; limit?: number } = {}) => {
  const q = new URLSearchParams();
  q.set("offset", String(params.offset ?? 0));
  q.set("limit", String(params.limit ?? 200));
  if (params.issue) q.set("issue", params.issue);
  return getApi(`${base}/karkhana/worker-review?${q.toString()}`);
};

export const resolveWorkerReviewRow = (rowId: string, body: Record<string, unknown>) =>
  postApi(`${base}/karkhana/worker-review/${rowId}/resolve`, body);

export const resolveWorkerReviewBulk = (body: Record<string, unknown>) =>
  postApi(`${base}/karkhana/worker-review/resolve-bulk`, body);

export const listRateCards = (params: { q?: string; group?: string; offset?: number; limit?: number } = {}) => {
  const q = new URLSearchParams();
  q.set("offset", String(params.offset ?? 0));
  q.set("limit", String(params.limit ?? 50));
  if (params.q) q.set("q", params.q);
  if (params.group) q.set("group", params.group);
  return getApi(`${base}/karkhana/rate-cards?${q.toString()}`);
};

export const createRateCard = (body: Record<string, unknown>) => postApi(`${base}/karkhana/rate-cards`, body);

export const getRateCard = (id: string) => getApi(`${base}/karkhana/rate-cards/${id}`);

export const updateRateCard = (id: string, body: Record<string, unknown>) =>
  putApi(`${base}/karkhana/rate-cards/${id}`, body);

export const getRateCardAudit = (id: string) => getApi(`${base}/karkhana/rate-cards/${id}/audit`);

export const startRateVersion = (id: string, body: Record<string, unknown>) =>
  postApi(`${base}/karkhana/rate-cards/${id}/versions`, body);

export const saveRateDraft = (id: string, versionId: string, body: Record<string, unknown>) =>
  putApi(`${base}/karkhana/rate-cards/${id}/versions/${versionId}`, body);

export const approveRateVersion = (id: string, versionId: string) =>
  postApi(`${base}/karkhana/rate-cards/${id}/versions/${versionId}/approve`, {});

export const discardRateVersion = (id: string, versionId: string) =>
  postApi(`${base}/karkhana/rate-cards/${id}/versions/${versionId}/discard`, {});

export const duplicateRateCard = (id: string, body: Record<string, unknown>) =>
  postApi(`${base}/karkhana/rate-cards/${id}/duplicate`, body);

export const listTimeActivities = () => getApi(`${base}/karkhana/time-activities`);

export const createTimeActivity = (body: Record<string, unknown>) =>
  postApi(`${base}/karkhana/time-activities`, body);

export const updateTimeActivity = (id: string, body: Record<string, unknown>) =>
  putApi(`${base}/karkhana/time-activities/${id}`, body);

export const listTimeRates = (personId?: string) =>
  getApi(`${base}/karkhana/time-rates${personId ? `?personId=${encodeURIComponent(personId)}` : ""}`);

export const createTimeRate = (body: Record<string, unknown>) => postApi(`${base}/karkhana/time-rates`, body);

export const saveTimeRate = (id: string, body: Record<string, unknown>) =>
  putApi(`${base}/karkhana/time-rates/${id}`, body);

export const approveTimeRate = (id: string) => postApi(`${base}/karkhana/time-rates/${id}/approve`, {});

export const discardTimeRate = (id: string) => postApi(`${base}/karkhana/time-rates/${id}/discard`, {});

export const getTimeRateAudit = (personId?: string) =>
  getApi(`${base}/karkhana/time-rates/audit${personId ? `?personId=${encodeURIComponent(personId)}` : ""}`);

export const previewTimePay = (body: Record<string, unknown>) => postApi(`${base}/karkhana/time-rates/preview`, body);

export const listLooms = () => getApi(`${base}/karkhana/looms`);

export const addLooms = (body: Record<string, unknown>) => postApi(`${base}/karkhana/looms`, body);

export const updateLoom = (id: string, body: Record<string, unknown>) => putApi(`${base}/karkhana/looms/${id}`, body);

export const tagLoomLocation = (loomId: string, body: Record<string, unknown>) =>
  postApi(`${base}/karkhana/looms/${loomId}/location`, body);

export const saveLoomPhoto = (loomId: string, body: Record<string, unknown>) =>
  postApi(`${base}/karkhana/looms/${loomId}/photo`, body);

export const listRegisteredLooms = () => getApi(`${base}/karkhana/looms/registered`);

export const linkLoomRegister = (loomId: string, geoLoomId: string) =>
  postApi(`${base}/karkhana/looms/${loomId}/link-register`, { geoLoomId });

export const checkSareeLength = (loomId: string, body: Record<string, unknown>) =>
  postApi(`${base}/karkhana/looms/${loomId}/check-in`, body);

export const setSareeLength = (loomId: string, body: Record<string, unknown>) =>
  postApi(`${base}/karkhana/looms/${loomId}/saree-length`, body);

export const getSareeNumbers = () => getApi(`${base}/karkhana/saree-numbers`);

export const setSareeStart = (sareeStart: number) => putApi(`${base}/karkhana/saree-numbers`, { sareeStart });

export const getRounds = (date?: string) =>
  getApi(`${base}/karkhana/rounds${date ? `?date=${encodeURIComponent(date)}` : ""}`);

export const startSaree = (loomId: string, body: Record<string, unknown>) =>
  postApi(`${base}/karkhana/looms/${loomId}/start-saree`, body);

export const changeLoomPeople = (loomId: string, body: Record<string, unknown>) =>
  postApi(`${base}/karkhana/looms/${loomId}/people`, body);

export const changeSareeDesign = (loomId: string, designId: string) =>
  postApi(`${base}/karkhana/looms/${loomId}/design`, { designId });

export const finishSaree = (loomId: string, body: Record<string, unknown>) =>
  postApi(`${base}/karkhana/looms/${loomId}/finish-saree`, body);

export const closeLoom = (loomId: string, body: Record<string, unknown>) =>
  postApi(`${base}/karkhana/looms/${loomId}/close`, body);

export const reopenLoom = (loomId: string, body: Record<string, unknown>) =>
  postApi(`${base}/karkhana/looms/${loomId}/reopen`, body);

export const previewLoomEntry = (body: Record<string, unknown>) => postApi(`${base}/karkhana/loom-entries/preview`, body);

export const saveLoomEntry = (body: Record<string, unknown>) => postApi(`${base}/karkhana/loom-entries`, body);

export const voidLoomEntry = (id: string, body: Record<string, unknown>) =>
  postApi(`${base}/karkhana/loom-entries/${id}/void`, body);

export const getApprovals = () => getApi(`${base}/karkhana/approvals`);

export const decideLoomEntry = (id: string, approve: boolean, body: Record<string, unknown> = {}) =>
  postApi(`${base}/karkhana/loom-entries/${id}/${approve ? "approve" : "reject"}`, body);

export const decideLoomClosure = (id: string, approve: boolean) =>
  postApi(`${base}/karkhana/loom-closures/${id}/${approve ? "approve" : "reject"}`, {});

export const getAttendance = (date?: string) =>
  getApi(`${base}/karkhana/attendance${date ? `?date=${encodeURIComponent(date)}` : ""}`);

export const savePunch = (body: Record<string, unknown>) => postApi(`${base}/karkhana/attendance/punches`, body);

export const switchWork = (body: Record<string, unknown>) => postApi(`${base}/karkhana/attendance/switch`, body);

export const voidPunch = (id: string, body: Record<string, unknown>) =>
  postApi(`${base}/karkhana/attendance/punches/${id}/void`, body);

/** Moves a saved In or Out to another time that day; the old one is kept on record as removed. */
export const changePunchTime = (id: string, body: { time: string; note?: string }) =>
  postApi(`${base}/karkhana/attendance/punches/${id}/time`, body);

export const getAttendanceApprovals = () => getApi(`${base}/karkhana/attendance/approvals`);

export const decideAttendanceDay = (personId: string, date: string, approve: boolean, body: Record<string, unknown> = {}) =>
  postApi(`${base}/karkhana/attendance/days/${personId}/${date}/${approve ? "approve" : "reject"}`, body);

export const getPayAccount = (personId: string, from?: string, to?: string) => {
  const q = new URLSearchParams();
  if (from) q.set("from", from);
  if (to) q.set("to", to);
  const qs = q.toString();
  return getApi(`${base}/karkhana/pay/people/${personId}${qs ? `?${qs}` : ""}`);
};

export const recordPayEntry = (body: Record<string, unknown>) => postApi(`${base}/karkhana/pay/entries`, body);

export const voidPayEntry = (id: string, body: Record<string, unknown>) =>
  postApi(`${base}/karkhana/pay/entries/${id}/void`, body);

export const listPaySheets = () => getApi(`${base}/karkhana/pay-sheets`);

export const createPaySheet = (from: string, to: string) => postApi(`${base}/karkhana/pay-sheets`, { from, to });

/** What a pay sheet for these dates would say, without making one (the Pay page's week). */
export const getPaySheetPreview = (from: string, to: string) =>
  getApi(`${base}/karkhana/pay-sheets/preview?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`);

export const getPaySheet = (id: string) => getApi(`${base}/karkhana/pay-sheets/${id}`);

export const refreshPaySheet = (id: string) => postApi(`${base}/karkhana/pay-sheets/${id}/refresh`, {});

/** Owner: a draft sheet (made by the AI assistant) becomes a pay sheet people can be paid from. */
export const finalisePaySheet = (id: string) => postApi(`${base}/karkhana/pay-sheets/${id}/finalise`, {});

/** Owner: throw a draft sheet away. */
export const discardPaySheet = (id: string) => postApi(`${base}/karkhana/pay-sheets/${id}/discard`, {});

export const paySheetPerson = (id: string, personId: string, body: Record<string, unknown>) =>
  postApi(`${base}/karkhana/pay-sheets/${id}/people/${personId}/paid`, body);

export const listSarees = (q?: string, status?: string) => {
  const p = new URLSearchParams();
  if (q) p.set("q", q);
  if (status) p.set("status", status);
  return getApi(`${base}/karkhana/sarees${p.toString() ? `?${p}` : ""}`);
};

/** Without productUniqueId: create a draft product for the saree. With it: link that product. */
export const sareeProduct = (pieceId: string, productUniqueId?: string) =>
  postApi(`${base}/karkhana/sarees/${pieceId}/product`, productUniqueId ? { productUniqueId } : {});

export const unlinkSareeProduct = (pieceId: string) => postApi(`${base}/karkhana/sarees/${pieceId}/unlink`, {});

export const setSareePublic = (pieceId: string, on: boolean) => postApi(`${base}/karkhana/sarees/${pieceId}/public`, { public: on });

export const getProductProduction = (productUniqueId: string) =>
  getApi(`${base}/karkhana/products/${encodeURIComponent(productUniqueId)}/production`);

/** Public product page: no login; returns visible=false unless the studio switched it on. */
export const getPublicProduction = async (productUniqueId: string) => {
  const res = await fetch(`${base}/karkhana/public/production/${encodeURIComponent(productUniqueId)}`);
  return res.json();
};

export const searchStudioProducts = (name: string) =>
  getApi(`${base}/product/products?offset=0&limit=10&name=${encodeURIComponent(name)}`);

export const getCashBook = (supervisor?: string) =>
  getApi(`${base}/karkhana/cash-book${supervisor ? `?supervisor=${encodeURIComponent(supervisor)}` : ""}`);

export const cashHandover = (supervisor: string, body: Record<string, unknown>) =>
  postApi(`${base}/karkhana/cash-book/handover?supervisor=${encodeURIComponent(supervisor)}`, body);

export const cashReturn = (supervisor: string | undefined, body: Record<string, unknown>) =>
  postApi(`${base}/karkhana/cash-book/return${supervisor ? `?supervisor=${encodeURIComponent(supervisor)}` : ""}`, body);

export const getKarkhanaReport = (from: string, to: string) =>
  getApi(`${base}/karkhana/reports?from=${from}&to=${to}`);

/** Download the Excel report (sent as a file, not JSON). */
export const downloadKarkhanaReport = async (from: string, to: string) => {
  const token = localStorage.getItem("token");
  const res = await fetch(`${base}/karkhana/reports/excel?from=${from}&to=${to}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  const type = res.headers.get("Content-Type") || "";
  if (!res.ok || !type.includes("spreadsheet")) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.message || "Could not make the Excel file");
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `karkhana-report-${from}-to-${to}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const listHistoryImports = () => getApi(`${base}/karkhana/history-imports`);

export const uploadHistoryImport = async (file: File) => {
  const form = new FormData();
  form.append("file", file);
  const token = localStorage.getItem("token");
  const res = await fetch(`${base}/karkhana/history-imports`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  return res.json();
};

export const historyImportRows = (id: string, issue: string | undefined, offset = 0) =>
  getApi(`${base}/karkhana/history-imports/${id}/rows?offset=${offset}&limit=50${issue ? `&issue=${issue}` : ""}`);

export const keepHistoryImport = (id: string) => postApi(`${base}/karkhana/history-imports/${id}/keep`, {});

export const discardHistoryImport = (id: string) => postApi(`${base}/karkhana/history-imports/${id}/discard`, {});

export const listSupervisors = () => getApi(`${base}/karkhana/supervisors`);

export const createSupervisor = (body: Record<string, unknown>) => postApi(`${base}/karkhana/supervisors`, body);

export const setSupervisorPassword = (id: string, password: string) =>
  postApi(`${base}/karkhana/supervisors/${id}/password`, { password });

export const setSupervisorPin = (id: string, pin: string) => postApi(`${base}/karkhana/supervisors/${id}/pin`, { pin });

/** Signs the supervisor out on every phone; they sign in again with the PIN. */
export const signOutSupervisorPhones = (id: string) => postApi(`${base}/karkhana/supervisors/${id}/sign-out`, {});

export const setSupervisorAccess = (id: string, active: boolean) =>
  postApi(`${base}/karkhana/supervisors/${id}/access`, { active });

/** The supervisor also works here and is paid like a worker (default category Office): links or adds their worker. */
export const makeSupervisorWorker = (id: string, categoryId?: string) =>
  postApi(`${base}/karkhana/supervisors/${id}/worker`, categoryId ? { categoryId } : {});

/** Owner edits a supervisor's name and/or mobile (the login and their worker change together). */
export const updateSupervisor = (id: string, body: { name?: string; phone?: string; note?: string }) =>
  putApi(`${base}/karkhana/supervisors/${id}`, body);

/** Maps this exact worker to the supervisor; a login-less user holding it is folded in. */
export const linkSupervisorWorker = (id: string, workerId: string, note?: string) =>
  postApi(`${base}/karkhana/supervisors/${id}/worker`, note ? { workerId, note } : { workerId });

/** Unmaps the supervisor's worker; the worker, attendance and pay stay. */
export const unlinkSupervisorWorker = (id: string, note?: string) =>
  deleteApi(`${base}/karkhana/supervisors/${id}/worker${note ? `?note=${encodeURIComponent(note)}` : ""}`, {});

/** What mapping a worker or folding in a login-less user would do, without writing. */
export const previewSupervisorMerge = (id: string, target: { userId?: string; workerId?: string }) => {
  const q = new URLSearchParams();
  if (target.userId) q.set("userId", target.userId);
  if (target.workerId) q.set("workerId", target.workerId);
  return getApi(`${base}/karkhana/supervisors/${id}/merge-user?${q.toString()}`);
};

/**
 * Folds a login-less Karkhana user into the supervisor (their worker moves, that login is retired).
 * `phone` (in the body, never the URL): the new mobile this fold is for; the server refuses before
 * folding if that phone change would still fail.
 */
export const mergeSupervisorUser = (id: string, userId: string, note?: string, phone?: string) =>
  postApi(`${base}/karkhana/supervisors/${id}/merge-user`, {
    userId,
    ...(note ? { note } : {}),
    ...(phone ? { phone } : {}),
  });

/** Owner sets a supervisor's usual round (looms and loom groups shown first to them). Empty lists clear it. */
export const setSupervisorRound = (id: string, body: { loomIds: string[]; loomGroups: string[] }) =>
  putApi(`${base}/karkhana/supervisors/${id}/round`, body);

/** The studio's manager logins, each with the worker they are paid as (owner only). */
export const listStudioManagers = () => getApi(`${base}/karkhana/supervisors/managers`);

/** Who changed this supervisor's name, phone or worker, oldest first. */
export const getSupervisorChanges = (id: string) => getApi(`${base}/karkhana/supervisors/${id}/changes`);

/** Job work: dyeing per lot, per colour, per saree… (quantity × agreed rate). */
export const listJobWork = (params: { from?: string; to?: string; person?: string; status?: string } = {}) => {
  const q = Object.entries(params)
    .filter(([, v]) => v)
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
    .join("&");
  return getApi(`${base}/karkhana/job-work${q ? `?${q}` : ""}`);
};

export const saveJobWork = (body: Record<string, unknown>) => postApi(`${base}/karkhana/job-work`, body);

export const decideJobWork = (id: string, approve: boolean, reason?: string) =>
  postApi(`${base}/karkhana/job-work/${encodeURIComponent(id)}/decide`, { approve, reason });

export const voidJobWork = (id: string, reason: string) =>
  postApi(`${base}/karkhana/job-work/${encodeURIComponent(id)}/void`, { reason });

/** One saree's days, weavers and cost so far; a loom's sarees over time. */
export const getSareeHistory = (pieceId: string) => getApi(`${base}/karkhana/sarees/${encodeURIComponent(pieceId)}/history`);

export const fixSareeWorkType = (pieceId: string, entryIds: string[], workTypeId: string) =>
  postApi(`${base}/karkhana/sarees/${encodeURIComponent(pieceId)}/work-type`, { entryIds, workTypeId });

export const getLoomHistory = (loomId: string) => getApi(`${base}/karkhana/looms/${encodeURIComponent(loomId)}/history`);

/** Everyone's balance today (wages due, advance owed); the Karkhana home dashboard. */
export const getPayLedger = () => getApi(`${base}/karkhana/pay/people`);

export const getKarkhanaDashboard = () => getApi(`${base}/karkhana/dashboard`);

/** Correct one loom entry: weaver, helper, inches or part. */
export const editLoomEntry = (id: string, body: Record<string, unknown>) =>
  putApi(`${base}/karkhana/loom-entries/${encodeURIComponent(id)}`, body);

/**
 * Owner/manager entry changes: remove, restore, move to another day or saree, or edit. Preview first
 * (checks every row, nothing written), then confirm with the preview's versions and a clientRequestId.
 */
export const previewEntryChanges = (body: Record<string, unknown>) => postApi(`${base}/karkhana/loom-entries/changes/preview`, body);

export const saveEntryChanges = (body: Record<string, unknown>) => postApi(`${base}/karkhana/loom-entries/changes`, body);

/** One entry's change log, including the entries it was moved from or to. */
export const getEntryChanges = (id: string) => getApi(`${base}/karkhana/loom-entries/${encodeURIComponent(id)}/changes`);

/** Opening balances (owner): what each account stood at on the day the studio moved onto HKP. */
export const getOpeningBalances = () => getApi(`${base}/karkhana/pay/openings`);

export const saveOpeningBalances = (body: { date: string; note: string; rows: Record<string, string | undefined>[] }) =>
  postApi(`${base}/karkhana/pay/openings`, body);

export const removeOpeningBalance = (personId: string, note: string) =>
  postApi(`${base}/karkhana/pay/openings/${encodeURIComponent(personId)}/remove`, { note });

/** Supervisors' requests to take money back, and the owner/manager decision. */
export const getPayVoidRequests = () => getApi(`${base}/karkhana/pay/void-requests`);

export const decidePayVoid = (id: string, approve: boolean, note?: string) =>
  postApi(`${base}/karkhana/pay/entries/${encodeURIComponent(id)}/void/${approve ? "approve" : "reject"}`, { note });

// ---- Saree costing (studio owner/admins and managers only) ----------------------------------

export const listCostings = (params: { status?: string; q?: string } = {}) => {
  const p = new URLSearchParams();
  if (params.status) p.set("status", params.status);
  if (params.q) p.set("q", params.q);
  return getApi(`${base}/karkhana/costings${p.toString() ? `?${p}` : ""}`);
};

/** For a saree (pieceId or sareeNumber) or, as an estimate, a design. A saree that has one returns it. */
export const createCosting = (body: { pieceId?: string; sareeNumber?: string; designId?: string }) =>
  postApi(`${base}/karkhana/costings`, body);

export const getCosting = (id: string) => getApi(`${base}/karkhana/costings/${encodeURIComponent(id)}`);

export const saveCosting = (id: string, body: Record<string, unknown>) =>
  putApi(`${base}/karkhana/costings/${encodeURIComponent(id)}`, body);

const costingAction = (id: string, action: string, body: Record<string, unknown> = {}) =>
  postApi(`${base}/karkhana/costings/${encodeURIComponent(id)}/${action}`, body);

export const refreshCostingPrefill = (id: string) => costingAction(id, "refresh-prefill");
export const submitCosting = (id: string) => costingAction(id, "submit");
export const withdrawCosting = (id: string) => costingAction(id, "withdraw");
export const approveCosting = (id: string, note?: string) => costingAction(id, "approve", { note });
export const rejectCosting = (id: string, reason: string) => costingAction(id, "reject", { reason });
export const reviseCosting = (id: string) => costingAction(id, "revise");
export const discardCosting = (id: string) => costingAction(id, "discard");
export const getCostingAudit = (id: string) => getApi(`${base}/karkhana/costings/${encodeURIComponent(id)}/audit`);
export const getSareeCosting = (pieceId: string) => getApi(`${base}/karkhana/sarees/${encodeURIComponent(pieceId)}/costing`);

export const getCostingTemplate = () => getApi(`${base}/karkhana/costing-template`);
export const saveCostingTemplate = (body: Record<string, unknown>) => putApi(`${base}/karkhana/costing-template`, body);
export const resetCostingTemplate = () => postApi(`${base}/karkhana/costing-template/reset`, {});

export const listMaterialRates = () => getApi(`${base}/karkhana/material-rates`);
export const addMaterialRate = (body: Record<string, unknown>) => postApi(`${base}/karkhana/material-rates`, body);
export const saveMaterialRate = (id: string, body: Record<string, unknown>) =>
  putApi(`${base}/karkhana/material-rates/${encodeURIComponent(id)}`, body);
export const getMaterialRateAudit = () => getApi(`${base}/karkhana/material-rates/audit`);
/** The costing of a product's saree (or of the saree it is a variant of), for the product page. */
export const getProductCosting = (productUniqueId: string) =>
  getApi(`${base}/karkhana/products/${encodeURIComponent(productUniqueId)}/costing`);

// ---- Day one: people added where they're needed, rates after the work ------------------------

/** Rows typed or pasted ("Ganesh weaver 98390 12345"), checked like a file. */
export const previewWorkerImportRows = (body: Record<string, unknown>) => postApi(`${base}/karkhana/worker-imports`, body);

/** The Excel template with the studio's own work types, as { fileName, base64 }. */
export const getWorkerImportTemplate = () => getApi(`${base}/karkhana/worker-imports/template`);


/** Work waiting for a rate: loom design + part, and people with attendance but no day rate. */
export const getNotCounted = () => getApi(`${base}/karkhana/pay/not-counted`);

/** Owner fills one missing loom rate; waiting entries from that day are paid. */
export const saveQuickRate = (body: Record<string, unknown>) => postApi(`${base}/karkhana/rate-cards/quick`, body);

export const workerPhotoUploadUrl = (workerId: string, fileType: string) =>
  postApi(`${base}/karkhana/worker-profiles/${workerId}/photo/upload-url`, { fileType });

export const attachWorkerPhoto = (workerId: string, key: string) =>
  putApi(`${base}/karkhana/worker-profiles/${workerId}/photo`, { key });

export const removeWorkerPhoto = (workerId: string) => deleteApi(`${base}/karkhana/worker-profiles/${workerId}/photo`);

/** Upload a face photo straight to private storage and save it on the worker. Returns the new photo link. */
export const uploadWorkerPhoto = async (workerId: string, file: Blob, fileType: string): Promise<string | null> => {
  const res: any = await workerPhotoUploadUrl(workerId, fileType);
  if (!res?.success || !res?.data?.uploadUrl) throw new Error(res?.message || "The photo didn't upload. Try again");
  const put = await fetch(res.data.uploadUrl, { method: "PUT", headers: { "Content-Type": fileType }, body: file });
  if (!put.ok) throw new Error("The photo didn't upload. Try again");
  const saved: any = await attachWorkerPhoto(workerId, res.data.key);
  if (!saved?.success) throw new Error(saved?.message || "The photo didn't save");
  return saved.data?.photoUrl || null;
};

/** A person's day rate from a day (owner): a time rate made and approved in one go. */
export const setPersonDayRate = async (personId: string, amount: string, effectiveFrom: string) => {
  const res: any = await createTimeRate({ personId, basis: "DAILY", amount, hoursPerDay: "8", effectiveFrom, note: "Set while adding the person" });
  if (!res?.success || !res?.data?.id) return res;
  return approveTimeRate(res.data.id);
};

// ---- Corrections after work is entered: saree start and number, loom code and retiring ---------

/** Would moving the saree's start to this day work? {currentStart, earliestAllowed, firstEntryDate, ok, problem}. Nothing changes. */
export const checkSareeStartDate = (pieceId: string, date: string) =>
  getApi(`${base}/karkhana/sarees/${encodeURIComponent(pieceId)}/start-check?date=${encodeURIComponent(date)}`);

/** Owner/manager: move the saree's "on the loom since" date (earlier or later). Logged with who and when. */
export const changeSareeStartDate = (pieceId: string, date: string, note?: string) =>
  postApi(`${base}/karkhana/sarees/${encodeURIComponent(pieceId)}/start`, { date, note });

/** Owner/manager: correct a saree's number, even to a lower one. The series never goes back. */
export const changeSareeNumber = (pieceId: string, sareeNumber: string, note?: string) =>
  putApi(`${base}/karkhana/sarees/${encodeURIComponent(pieceId)}/number`, { sareeNumber, note });

/** Owner: give a loom a new number; its history and old number stay. */
export const renameLoom = (loomId: string, code: string, note?: string) =>
  postApi(`${base}/karkhana/looms/${encodeURIComponent(loomId)}/rename`, { code, note });

/** Owner: hide a loom from the rounds and pickers for good; its history stays. */
export const retireLoom = (loomId: string, note?: string) =>
  postApi(`${base}/karkhana/looms/${encodeURIComponent(loomId)}/retire`, { note });

/** A loom's renames and retirement: who, when, old → new. */
export const getLoomChanges = (loomId: string) => getApi(`${base}/karkhana/looms/${encodeURIComponent(loomId)}/changes`);

// ---- Rate drafts waiting for approval, approved together -----------------------------------------

/** Drafts by design, each with onActiveLoom and its dated drafts. */
export const listRateDrafts = () => getApi(`${base}/karkhana/rate-cards/drafts`);

/** Owner: approve every draft of these designs, in date order. Returns {approved, notApproved, results[]}. */
export const approveRateDrafts = (designIds: string[]) => postApi(`${base}/karkhana/rate-cards/approve-drafts`, { designIds });

/** Owner: approve only designs with a saree on an active loom (narrowed to designIds when given). */
export const approveRateDraftsOnActiveLooms = (designIds: string[] = []) =>
  postApi(`${base}/karkhana/rate-cards/approve-drafts/active-looms`, designIds.length ? { designIds } : {});
